import api from '@/services/api';

export interface UkAddressOption {
  line_1: string;
  line_2?: string;
  line_3?: string;
  post_town: string;
  county?: string;
  postcode: string;
  formatted: string;
  address: string;
  city: string;
}

export interface UkPostcodeResult {
  source: 'ideal' | 'postcodes_io';
  postcode: string;
  province: string;
  city: string;
  region: string;
  addresses: UkAddressOption[];
  fallback?: boolean;
  ideal_error?: string;
}

function frappeMessageError(err: unknown): string {
  const anyErr = err as { response?: { data?: { message?: string; _server_messages?: string } }; message?: string };
  const msg = anyErr?.response?.data?.message;
  if (typeof msg === 'string' && msg) return msg;
  try {
    const raw = anyErr?.response?.data?._server_messages;
    if (raw) {
      const parsed = JSON.parse(raw);
      const first = typeof parsed?.[0] === 'string' ? JSON.parse(parsed[0]) : parsed?.[0];
      if (first?.message) return String(first.message);
    }
  } catch {
    /* ignore */
  }
  return anyErr?.message || 'Postcode lookup failed';
}

/** UK postcode / street lookup via SaleDesk backend (Ideal if key set, else postcodes.io). */
export async function lookupUkPostcode(postcode: string): Promise<UkPostcodeResult> {
  const cleaned = (postcode || '').trim();
  if (!cleaned) throw new Error('Enter a postcode');

  try {
    const res = await api.get('/method/saas_pos.api.uk_address.lookup_uk_postcode', {
      params: { postcode: cleaned },
    });
    const msg = res.data?.message;
    if (!msg || typeof msg !== 'object') {
      throw new Error('Postcode lookup failed');
    }
    return {
      source: msg.source === 'ideal' ? 'ideal' : 'postcodes_io',
      postcode: String(msg.postcode || cleaned),
      province: String(msg.province || ''),
      city: String(msg.city || ''),
      region: String(msg.region || msg.province || ''),
      addresses: Array.isArray(msg.addresses) ? msg.addresses : [],
      fallback: Boolean(msg.fallback),
      ideal_error: msg.ideal_error ? String(msg.ideal_error) : undefined,
    };
  } catch (err) {
    throw new Error(frappeMessageError(err));
  }
}

export async function testIdealPostcodesKey(apiKey?: string, postcode = 'PR1 3BU') {
  try {
    const res = await api.post('/method/saas_pos.api.uk_address.test_ideal_postcodes_key', {
      api_key: apiKey || '',
      postcode,
    });
    return res.data?.message as {
      ok: boolean;
      postcode?: string;
      address_count?: number;
      sample?: UkAddressOption;
    };
  } catch (err) {
    throw new Error(frappeMessageError(err));
  }
}
